import PropTypes from 'prop-types';
import * as React from 'react';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { deprecatedComponent } from '../core/deprecated-component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { RenderComp } from '../core/render-comp.component.js';

const propTypes = {
    /**
   * Specifies if the StandardHero should have a full width image
   */ fullWidthImage: PropTypes.bool,
    /**
   * Height of the StandardHero
   * Available options: "standard", "short"
   */ height: PropTypes.oneOf([
        'standard',
        'short'
    ]),
    /**
   * Visual style of the StandardHero
   * Available options: "standard", "holiday"
   */ skin: PropTypes.oneOf([
        'standard',
        'holiday'
    ]),
    /**
   * Specifies if the image and text should be staggered or not
   */ staggered: PropTypes.bool,
    /**
   * How text should be aligned horizontally within the StandardHero.
   * Example: In the basic StandardHero (non-fullWidthImage), setting the value "right" will move the image to the left and the text to the right.
   * Available options: "left", "center", "right"
   */ textHorizontalAlign: PropTypes.oneOf([
        'left',
        'center',
        'right'
    ]),
    // TODO: textBackground=transparent, and no backgroundColor, then set the backgroundColor as 'transparent'
    // add deprecation warning
    /**
   * Background of the text
   * Available options: "standard", "transparent"
   */ textBackground: PropTypes.oneOf([
        'standard',
        'transparent'
    ]),
    /**
   * How text should be aligned vertically within the StandardHero
   * Available options: "center", "top", "bottom"
   */ textVerticalAlign: PropTypes.oneOf([
        'center',
        'top',
        'bottom'
    ]),
    /**
   * Specifies if the StandardHero's layout should adjust to fit a left hand navigation menu
   */ withNav: PropTypes.bool,
    /**
   * Specifies if the StandardHero's image should be hidden on mobile
   */ hideImageOnMobile: PropTypes.bool,
    /**
   * Specifies if the StandardHero's text should display behind the text on mobile
   */ imageBehindTextOnMobile: PropTypes.bool
};
const propKeysToRemove = Object.keys(propTypes);
// TODO
/*
 * @deprecated use `Banner` component instead
 */ const StandardHero = deprecatedComponent({
    displayName: 'StandardHero',
    description: 'Use `Banner` component instead.',
    root: renderWithRef('StandardHero', propTypes, (props, ref)=>{
        useComponentStylesLoaded('StandardHero', SWAN_STYLE_KEY_MAP.standardHero);
        const { children, fullWidthImage, height, skin, staggered, textHorizontalAlign, textBackground, textVerticalAlign, withNav, hideImageOnMobile, imageBehindTextOnMobile } = props;
        const classNameSet = new Set([
            'swan-standard-hero'
        ]);
        if (skin !== 'standard') classNameSet.add(`swan-standard-hero-skin-${skin}`);
        if (textVerticalAlign !== 'center') classNameSet.add(`swan-standard-hero-text-vertical-align-${textVerticalAlign}`);
        if (textHorizontalAlign !== 'left') classNameSet.add(`swan-standard-hero-text-align-${textHorizontalAlign}`);
        if (textBackground === 'transparent') classNameSet.add('swan-standard-hero-text-transparent-background');
        if (fullWidthImage) classNameSet.add('swan-standard-hero-full-width-image');
        if (staggered) classNameSet.add('swan-standard-hero-staggered');
        if (height === 'short') classNameSet.add('swan-standard-hero-short');
        if (withNav) classNameSet.add('swan-standard-hero-with-left-hand-navigation');
        if (hideImageOnMobile) classNameSet.add('swan-standard-hero-hide-image-on-mobile');
        if (imageBehindTextOnMobile) classNameSet.add('swan-standard-hero-image-behind-text-on-mobile');
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(RenderComp, {
            root: "div",
            forwardedRef: ref,
            propKeysToRemove: propKeysToRemove,
            classNames: classNameSet,
            props: props
        }, children));
    })
});
StandardHero.defaultProps = {
    fullWidthImage: false,
    height: 'standard',
    skin: 'standard',
    staggered: false,
    textHorizontalAlign: 'left',
    textBackground: 'standard',
    textVerticalAlign: 'center',
    withNav: false
};

export { StandardHero };
